import { msalInstance } from '../msalInstance';
import { loginRequest } from '../msalConfig';

// 로그인 진행 중 중복 트리거 방지
let loginRedirectInFlight = false;

async function refreshAuthToken() {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts || accounts.length === 0) return null;
  try {
    // forceRefresh: true — MSAL cache 의 만료된 idToken 반환 회피.
    // 이전 default 동작 (cache hit) 시 stale 만료 토큰 반복 사용되어
    // 'jwt expired' 401 무한 루프 발생. force 로 Azure AD 재요청.
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: msalInstance.getActiveAccount() || accounts[0],
      forceRefresh: true,
    });
    if (response?.idToken) {
      localStorage.setItem('authToken', response.idToken);
      return response.idToken;
    }
    return null;
  } catch {
    return null;
  }
}

function buildUrl(path, query) {
  if (!query) return path;
  const qs = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    qs.append(k, String(v));
  });
  const s = qs.toString();
  return s ? `${path}${path.includes('?') ? '&' : '?'}${s}` : path;
}

function mergeHeaders(base, extra) {
  const h = new Headers(base || undefined);
  if (extra) {
    Object.entries(extra).forEach(([k, v]) => {
      if (v !== undefined && v !== null) h.set(k, v);
    });
  }
  return h;
}

async function parseResponse(res) {
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    try { return await res.json(); } catch { return null; }
  }
  return null;
}

async function handleUnauthorized() {
  // 게스트 모드 — MSAL 없음. 401 나면 이름을 다시 받도록 게스트 이름만 비우고 리로드.
  if (process.env.REACT_APP_AUTH_MODE === 'guest') {
    localStorage.removeItem('guestName');
    if (!loginRedirectInFlight) { loginRedirectInFlight = true; window.location.reload(); }
    return;
  }
  // 로컬 토큰 즉시 제거 (이후 어떤 요청도 옛 토큰을 보내지 않도록)
  localStorage.removeItem('authToken');

  if (loginRedirectInFlight) return;
  loginRedirectInFlight = true;

  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts && accounts.length > 0) {
      // MSAL 세션이 살아있으면 silent refresh 한 번 더 시도 (조기 만료 케이스 보호)
      // forceRefresh: true — cache 의 만료 토큰 회피.
      try {
        const response = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account: msalInstance.getActiveAccount() || accounts[0],
          forceRefresh: true,
        });
        if (response?.idToken) {
          localStorage.setItem('authToken', response.idToken);
          loginRedirectInFlight = false;
          window.location.reload(); // 새 토큰으로 재진입
          return;
        }
      } catch (_) { /* fall through to redirect */ }
    }
    // silent 실패 또는 세션 없음 → Microsoft 로그인 페이지로 redirect
    await msalInstance.loginRedirect(loginRequest);
  } catch (e) {
    console.error('로그인 redirect 실패:', e);
    loginRedirectInFlight = false;
  }
}

function handleForbidden(payload) {
  const msg = payload?.error?.message || '접근 권한이 없습니다.';
  window.dispatchEvent(new CustomEvent('api:forbidden', { detail: { message: msg } }));
}

export class ApiError extends Error {
  constructor(status, code, message, payload) {
    super(message || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

export async function request(path, options = {}) {
  const {
    method = 'GET',
    query,
    body,
    headers,
    signal,
    raw = false,
    skipAuth = false,
  } = options;

  const url = buildUrl(path, query);
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const finalHeaders = mergeHeaders(
    headers,
    {
      ...(isFormData || body === undefined ? {} : { 'Content-Type': 'application/json' }),
    }
  );

  if (!skipAuth) {
    if (process.env.REACT_APP_AUTH_MODE === 'guest') {
      // 게스트 모드 — 토큰 대신 이름 헤더(서버 verifyJwt 가 X-Guest-Name 을 신원으로).
      //   한글 이름 대비 encodeURIComponent (HTTP 헤더는 latin1) → 서버가 decode.
      const gname = localStorage.getItem('guestName');
      if (gname && !finalHeaders.has('X-Guest-Name')) {
        finalHeaders.set('X-Guest-Name', encodeURIComponent(gname));
      }
    } else {
      const token = localStorage.getItem('authToken');
      if (token && !finalHeaders.has('Authorization')) {
        finalHeaders.set('Authorization', `Bearer ${token}`);
      }
    }
  }

  const init = {
    method,
    headers: finalHeaders,
    signal,
  };
  if (body !== undefined) {
    init.body = isFormData || typeof body === 'string' ? body : JSON.stringify(body);
  }

  const res = await fetch(url, init);

  if (raw) return res;

  if (res.status === 401) {
    if (!options._retry && !skipAuth) {
      const refreshed = await refreshAuthToken();
      if (refreshed) {
        return request(path, { ...options, _retry: true });
      }
    }
    handleUnauthorized();
    const payload = await parseResponse(res);
    throw new ApiError(401, payload?.error?.code || 'UNAUTHORIZED', payload?.error?.message || '인증이 필요합니다.', payload);
  }

  if (res.status === 403) {
    const payload = await parseResponse(res);
    handleForbidden(payload);
    throw new ApiError(403, payload?.error?.code || 'FORBIDDEN', payload?.error?.message || '권한이 없습니다.', payload);
  }

  if (!res.ok) {
    const payload = await parseResponse(res);
    throw new ApiError(
      res.status,
      payload?.error?.code || 'HTTP_ERROR',
      payload?.error?.message || payload?.message || `요청 실패 (${res.status})`,
      payload
    );
  }

  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    return res.json();
  }
  return res;
}

export const api = {
  get: (path, options = {}) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options = {}) => request(path, { ...options, method: 'POST', body }),
  put: (path, body, options = {}) => request(path, { ...options, method: 'PUT', body }),
  del: (path, options = {}) => request(path, { ...options, method: 'DELETE' }),
};
